import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import DataTable from "react-data-table-component";
import { useNavigate } from "react-router-dom";
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Spinner,
  Badge,
  Form,
  InputGroup,
} from "react-bootstrap";
import Header_Manager from "../../Components/Header_Manager";
import { BASE_URL } from "../../Config";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap-icons/font/bootstrap-icons.css";
import "./Manager.css";

const extractCompanyList = (payload) => {
  if (!payload) return [];
  if (Array.isArray(payload.companyDetails)) return payload.companyDetails;
  if (Array.isArray(payload.CompanyDetails)) return payload.CompanyDetails;
  if (Array.isArray(payload.data)) return payload.data;
  if (Array.isArray(payload.companies)) return payload.companies;
  if (Array.isArray(payload)) return payload;
  return [];
};

const Manager_Dashboard = () => {
  const navigate = useNavigate();
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [managerName, setManagerName] = useState("");
  const [managerId, setManagerId] = useState("");
  const [search, setSearch] = useState("");

  const fetchAssignedCompanies = useCallback(async (id) => {
    if (!id) return;
    setLoading(true);
    try {
      const response = await axios.get(`${BASE_URL}AssignedCompany?ManagerId=${id}`, {
        headers: { Accept: "*/*" },
      });

      const list = extractCompanyList(response.data);
      const ok =
        response.data?.responseCode === "0000" ||
        response.data?.ResponseCode === "0000" ||
        list.length > 0;

      if (ok) {
        setCompanies(list);

        const user = JSON.parse(sessionStorage.getItem("user") || "{}");
        sessionStorage.setItem(
          "user",
          JSON.stringify({
            ...user,
            assignedCompanies: list,
          })
        );
      } else {
        setCompanies([]);
        Swal.fire(
          "Info",
          response.data?.message ||
            response.data?.Message ||
            "No companies are currently assigned.",
          "info"
        );
      }
    } catch (error) {
      console.error("AssignedCompany error:", error);
      setCompanies([]);
      Swal.fire("Error", "Failed to fetch assigned companies.", "error");
    } finally {
      setLoading(false);
    }
  }, []);

  const openCompanyDashboard = (company) => {
    const companyId = company.companyProfileId || company.CompanyProfileId;
    if (!companyId) {
      Swal.fire(
        "Error",
        "Unable to open this company. Company identifier is missing.",
        "error"
      );
      return;
    }

    const user = JSON.parse(sessionStorage.getItem("user") || "{}");
    sessionStorage.setItem(
      "user",
      JSON.stringify({
        ...user,
        companyId,
        companyName: company.name || company.Name || "",
        selectedCompanyId: companyId,
        selectedCompanyName: company.name || company.Name || "",
      })
    );

    navigate("/Company/Company_Dashboard");
  };

  useEffect(() => {
    const storedUser = sessionStorage.getItem("user");
    if (!storedUser) {
      Swal.fire("Error", "Your session has expired. Please sign in again.", "error");
      return;
    }

    const user = JSON.parse(storedUser);
    setManagerName(user.name || user.email || "");
    setManagerId(user.userId || "");

    if (Array.isArray(user.assignedCompanies) && user.assignedCompanies.length > 0) {
      setCompanies(user.assignedCompanies);
    }

    if (user.userId) {
      fetchAssignedCompanies(user.userId);
    }
  }, [fetchAssignedCompanies]);

  const filteredCompanies = companies.filter((c) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return (
      String(c.name || c.Name || "")
        .toLowerCase()
        .includes(q) ||
      String(c.contactNumber || c.ContactNumber || "")
        .toLowerCase()
        .includes(q) ||
      String(c.productSale || c.ProductSale || "")
        .toLowerCase()
        .includes(q)
    );
  });

  const columns = [
    {
      name: "Company",
      selector: (row) => row.name ?? row.Name ?? "-",
      sortable: true,
      grow: 2,
      wrap: true,
    },
    {
      name: "Contact",
      selector: (row) => row.contactNumber ?? row.ContactNumber ?? "-",
      sortable: true,
      hide: "sm",
    },
    {
      name: "Product Sale",
      selector: (row) => row.productSale ?? row.ProductSale ?? "-",
      sortable: true,
      hide: "md",
    },
    {
      name: "Action",
      cell: (row) => (
        <Button
          size="sm"
          variant="primary"
          className="w-100"
          onClick={() => openCompanyDashboard(row)}
        >
          <i className="bi bi-box-arrow-up-right me-1" />
          Dashboard
        </Button>
      ),
      ignoreRowClick: true,
      minWidth: "140px",
    },
  ];

  return (
    <div className="mgr-page">
      <Header_Manager />

      <Container fluid="xl" className="px-3 px-md-4">
        <div className="mgr-hero d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3">
          <div>
            <h4 className="fw-semibold">
              <i className="bi bi-buildings me-2" />
              Assigned Companies
            </h4>
            <p>
              Welcome{managerName ? `, ${managerName}` : ""}. All companies assigned to
              you are listed below.
            </p>
          </div>
          <div className="d-flex flex-wrap gap-2">
            <Badge bg="light" text="dark" className="px-3 py-2">
              {companies.length} Compan{companies.length === 1 ? "y" : "ies"}
            </Badge>
            <Button
              variant="outline-light"
              size="sm"
              onClick={() => fetchAssignedCompanies(managerId)}
              disabled={loading || !managerId}
            >
              {loading ? (
                <>
                  <Spinner animation="border" size="sm" className="me-1" /> Refreshing
                </>
              ) : (
                <>
                  <i className="bi bi-arrow-clockwise me-1" /> Refresh
                </>
              )}
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={() => navigate("/Manager/Manager_Register")}
            >
              <i className="bi bi-plus-lg me-1" /> Register
            </Button>
          </div>
        </div>

        <Card className="mgr-card mb-3">
          <Card.Body className="py-3">
            <InputGroup>
              <InputGroup.Text>
                <i className="bi bi-search" />
              </InputGroup.Text>
              <Form.Control
                placeholder="Search by company name, contact, or product..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </InputGroup>
          </Card.Body>
        </Card>

        {/* Mobile cards */}
        <div className="d-md-none">
          {loading && (
            <div className="text-center py-5">
              <Spinner animation="border" />
              <p className="text-muted mt-2 mb-0">Loading assigned companies...</p>
            </div>
          )}
          {!loading && filteredCompanies.length === 0 && (
            <Card className="mgr-card text-center p-4">
              <p className="mb-0 text-muted">No assigned companies found</p>
            </Card>
          )}
          <Row className="g-3">
            {filteredCompanies.map((company) => (
              <Col
                xs={12}
                key={company.companyProfileId || company.CompanyProfileId || company.name}
              >
                <Card className="mgr-company-card">
                  <Card.Body>
                    <div className="d-flex justify-content-between align-items-start mb-2">
                      <h5 className="mb-0">{company.name || company.Name || "-"}</h5>
                      <span className="badge mgr-badge-soft rounded-pill">
                        {company.productSale || company.ProductSale || "N/A"}
                      </span>
                    </div>
                    <p className="text-muted small mb-1">
                      <i className="bi bi-telephone me-1" />
                      {company.contactNumber || company.ContactNumber || "-"}
                    </p>
                    <p className="text-muted small mb-3">
                      <i className="bi bi-briefcase me-1" />
                      {company.productSale || company.ProductSale || "N/A"}
                    </p>
                    <Button className="w-100" onClick={() => openCompanyDashboard(company)}>
                      View Dashboard
                    </Button>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        </div>

        {/* Desktop table */}
        <Card className="mgr-card d-none d-md-block">
          <Card.Header className="d-flex justify-content-between align-items-center">
            <strong>
              <i className="bi bi-list-ul me-2 text-primary" />
              Companies Assigned to Manager
            </strong>
            <span className="badge text-bg-light border">
              Showing {filteredCompanies.length} of {companies.length}
            </span>
          </Card.Header>
          <Card.Body className="p-0 p-md-3">
            <DataTable
              columns={columns}
              data={filteredCompanies}
              progressPending={loading}
              pagination
              paginationPerPage={10}
              highlightOnHover
              responsive
              striped
              noDataComponent="No assigned companies found"
            />
          </Card.Body>
        </Card>
      </Container>
    </div>
  );
};

export default Manager_Dashboard;
